import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { ChangeEvent, useRef, useState } from 'react';
import { WelcomeTemplateEntity } from '../../../../../types';
import { Editor } from 'tinymce';
import TextEditor from '../../../../shared/editor/text-editor';
import SharedEmailAutocomplete from '../../../../shared/shared-email-autocomplete';
import { UserFormValues } from '../user-schema';
import { DateTimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { replaceWelcomeTokens } from '../../../administration/settings/welcome-templates/welcome-template-tokens';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import Fade from '@mui/material/Fade';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import FormHelperText from '@mui/material/FormHelperText';

interface StepWelcomeRecipientsProps {
  setValue: UseFormSetValue<UserFormValues>;
  getValues: UseFormGetValues<UserFormValues>;
  watch: UseFormWatch<UserFormValues>;
  isLoading: boolean;
  control: Control<UserFormValues>;
  templates: WelcomeTemplateEntity[];
}

export default function StepWelcomeRecipients({
  getValues,
  setValue,
  isLoading,
  watch,
  control,
  templates,
}: StepWelcomeRecipientsProps) {
  const [multiAutoCompleteValue, setMultiAutoCompleteValue] = useState(() =>
    getValues('usersToNotify')
  );
  const [multiAutocompleteCcTo, setMultiAutocompleteCcTo] = useState(() =>
    getValues('ccTo')
  );
  const [multiAutoCompleteResponseBccTo, setMultiAutoCompleteResponseBccTo] =
    useState(() => getValues('bccTo'));
  // const [includeCreatedUser, setIncludeCreatedUser] = useState(true);

  const templateHtml = watch('welcomeEmailModel.templateHtml');
  const [
    showAdditonalConfigurationOptions,
    setShowAdditionalConfigurationOptions,
  ] = useState(false);

  function setHtmlBody() {
    if (editorRef.current) {
      setValue(
        'welcomeEmailModel.templateHtml',
        editorRef.current.getContent(),
        {
          shouldDirty: true,
        }
      );
    }
  }

  const editorRef = useRef<Editor | null>(null);
  const checked = watch('welcomeEmailModel.sendEmail');

  const usersToNotify = watch('usersToNotify')!;
  const userName = watch('userName');
  const domain = watch('domain');
  const email = `${userName}@${domain}`;
  const firstName = watch('name.givenName');
  const lastName = watch('name.familyName');
  const password = watch('password');
  const ccTo = watch('ccTo') || [];
  const bccTo = watch('bccTo') || [];
  const emailLength = ccTo.length + bccTo.length;
  const includeCreatedUser = watch('includeCreatedUser');

  function getSelectedGroupEmails() {
    return (getValues('groups') || []).map((group) => group.groupId);
  }

  const handleSendEmailCheckbox = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked === false) {
      setValue('usersToNotify', []);
      setValue('ccTo', []);
      setValue('bccTo', []);
      setValue('scheduledSendDate', null);
      setMultiAutoCompleteValue([]);
      setMultiAutocompleteCcTo([]);
      setMultiAutoCompleteResponseBccTo([]);
      setShowAdditionalConfigurationOptions(false);
      setValue('includeCreatedUser', false);
    } else {
      setValue('includeCreatedUser', true);
      setValue('usersToNotify', [email]);
    }
  };

  const handleNotifyUserCheckbox = (e: ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setValue('includeCreatedUser', isChecked);

    const currentUsers = getValues('usersToNotify') || [];

    if (isChecked) {
      setValue('usersToNotify', [...new Set([...currentUsers, email])]);
    } else {
      setValue(
        'usersToNotify',
        currentUsers.filter((user) => user !== email)
      );
    }
  };

  const handleTemplateType = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const template = templates.find(
      (template) => template.id.toString() === e.target.value
    );

    if (template) {
      const tokenValues = {
        domain,
        firstName,
        lastName,
        email,
        password,
        groupEmails: getSelectedGroupEmails(),
      };
      setValue(
        'welcomeEmailModel.templateHtml',
        replaceWelcomeTokens(template.templateHtml, tokenValues)
      );
      setValue(
        'welcomeEmailModel.templateSubject',
        replaceWelcomeTokens(template.templateSubject, tokenValues, 'text')
      );
      setValue('welcomeEmailModel.templateType', template.id.toString());
    } else {
      // Clear the template fields if the selection is removed
      setValue('welcomeEmailModel.templateHtml', '');
      setValue('welcomeEmailModel.templateSubject', '');
      setValue('welcomeEmailModel.templateType', '');
    }
  };

  return (
    <Stack justifyContent="flex-start" sx={{ height: '100%', width: '100%' }}>
      <Stack flexDirection="row">
        <Controller
          name="welcomeEmailModel.sendEmail"
          control={control}
          render={({ field: props }) => (
            <FormControlLabel
              control={
                <Checkbox
                  {...props}
                  checked={props.value}
                  onChange={(e) => {
                    props.onChange(e.target.checked);
                    handleSendEmailCheckbox(e);
                  }}
                />
              }
              label="Send Welcome Email"
            />
          )}
        />

        <FormControlLabel
          control={
            <Checkbox
              disabled={!checked}
              checked={includeCreatedUser}
              onChange={handleNotifyUserCheckbox}
            />
          }
          label={`Send to ${email}`}
        />
        <Controller
          name="scheduledSendDate"
          control={control}
          render={({ field, fieldState }) => (
            <DateTimePicker
              {...field}
              label="Scheduled Send Date"
              value={field.value ? dayjs(field.value) : null}
              disabled={!checked}
              disablePast
              onChange={(date) => field.onChange(date ? date.toDate() : null)}
              slotProps={{
                textField: {
                  size: 'small',
                  variant: 'outlined',
                  error: !!fieldState.error,
                  sx: {
                    width: 250,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'background.paper',
                    },
                  },
                },
              }}
            />
          )}
        />
      </Stack>
      <Stack flexDirection="column" gap={2} mb={4}>
        <Stack
          flexDirection="row"
          justifyContent="flex-start"
          alignItems="flex-start"
          gap={1}
          sx={{ mt: 1 }}
        >
          <Controller
            name={'usersToNotify'}
            control={control}
            render={({ fieldState: { error } }) => (
              <Stack
                flexDirection="column"
                alignItems="flex-start"
                sx={{ minWidth: 400 }}
              >
                <SharedEmailAutocomplete
                  freeSolo
                  sendType="To"
                  sources={['users', 'groups']}
                  createdEmail={email}
                  multiAutoCompleteValue={multiAutoCompleteValue}
                  setMultiAutoCompleteValue={(newEmails) => {
                    setMultiAutoCompleteValue(newEmails);
                    setValue('usersToNotify', [...new Set(newEmails)], {
                      shouldDirty: true,
                    });
                  }}
                  disabled={!checked}
                  includeCreatedUser={includeCreatedUser}
                />
                {error && usersToNotify.length === 0 && (
                  <FormHelperText
                    error={!!error && usersToNotify.length === 0}
                    sx={{ pl: 2 }}
                  >
                    {error.message}
                  </FormHelperText>
                )}
              </Stack>
            )}
          />
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={() =>
              setShowAdditionalConfigurationOptions(
                !showAdditonalConfigurationOptions
              )
            }
            disabled={!checked}
            sx={{
              cursor: checked ? 'pointer' : 'default',
              fontSize: '0.875rem',
              color: (theme) =>
                checked
                  ? theme.palette.semantic.hyperlink.active
                  : theme.palette.semantic.hyperlink.disabled,
              mt: 1,
            }}
          >
            {showAdditonalConfigurationOptions
              ? 'Hide Cc/Bcc'
              : `Show Cc/Bcc${emailLength ? ` (${emailLength})` : ''}`}
          </Link>
        </Stack>

        {showAdditonalConfigurationOptions ? (
          <>
            <Stack gap={1} flexDirection="row" alignItems="center">
              <Stack flexDirection={'row'} gap={1} sx={{ minWidth: 400 }}>
                <Controller
                  name={'ccTo'}
                  control={control}
                  render={() => (
                    <>
                      <SharedEmailAutocomplete
                        freeSolo
                        sendType="Cc"
                        sources={['users', 'groups']}
                        multiAutoCompleteValue={multiAutocompleteCcTo}
                        setMultiAutoCompleteValue={(newEmails) => {
                          setMultiAutocompleteCcTo(newEmails);
                          setValue('ccTo', [...new Set(newEmails)], {
                            shouldDirty: true,
                          });
                        }}
                        disabled={!checked}
                      />
                    </>
                  )}
                />
              </Stack>
            </Stack>
            <Stack gap={1} flexDirection="row" alignItems="center">
              <Stack flexDirection={'row'} gap={1} sx={{ minWidth: 400 }}>
                <Controller
                  name={'bccTo'}
                  control={control}
                  render={() => (
                    <>
                      <SharedEmailAutocomplete
                        freeSolo
                        sendType="Bcc"
                        sources={['users', 'groups']}
                        multiAutoCompleteValue={multiAutoCompleteResponseBccTo}
                        setMultiAutoCompleteValue={(newEmails) => {
                          setMultiAutoCompleteResponseBccTo(newEmails);
                          setValue('bccTo', [...new Set(newEmails)], {
                            shouldDirty: true,
                          });
                        }}
                        disabled={!checked}
                      />
                    </>
                  )}
                />
              </Stack>
            </Stack>
          </>
        ) : null}
        <Divider />
      </Stack>

      <Fade in={checked}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Stack flexDirection="row" gap={2} sx={{ mb: 1 }}>
            {isLoading ? (
              <>
                <Skeleton width={400} height={40} variant="rounded" />
                <Skeleton width={400} height={40} variant="rounded" />
              </>
            ) : (
              <>
                <Controller
                  name="welcomeEmailModel.templateSubject"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      autoComplete="off"
                      label="Subject"
                      size="small"
                      sx={{ minWidth: 400 }}
                    />
                  )}
                />

                <Controller
                  name="welcomeEmailModel.templateType"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      onChange={handleTemplateType}
                      inputProps={{
                        id: 'template',
                      }}
                      InputLabelProps={{
                        htmlFor: 'template',
                      }}
                      select
                      size="small"
                      label="Template"
                      sx={{ minWidth: 400 }}
                    >
                      {getValues('welcomeEmailModel.availableTemplates').map(
                        (domain) =>
                          domain === 'default' ? (
                            <MenuItem key={domain} value={domain}>
                              Default
                            </MenuItem>
                          ) : (
                            <MenuItem key={domain} value={domain}>
                              {
                                templates.find(
                                  (template) =>
                                    template.id.toString() === domain
                                )?.templateName
                              }
                            </MenuItem>
                          )
                      )}
                    </TextField>
                  )}
                />
              </>
            )}
          </Stack>
          {isLoading ? (
            <Skeleton height={300} variant="rounded" />
          ) : (
            <Box sx={{ flexGrow: 1, minHeight: 300 }}>
              <TextEditor
                editorRef={editorRef}
                content={templateHtml}
                setContent={setHtmlBody}
              />
            </Box>
          )}
        </Box>
      </Fade>
    </Stack>
  );
}
